import type { DocumentStatus, TemplateStatus } from '../../shared/api';
import { getLocale, t } from './i18n';

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(getLocale(), { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(getLocale(), { dateStyle: 'medium' });
}

const rtf = () => new Intl.RelativeTimeFormat(getLocale(), { numeric: 'auto' });

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  if (abs < 45) return t('just now');
  if (abs < 3600) return rtf().format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf().format(Math.round(diff / 3600), 'hour');
  if (abs < 86400 * 30) return rtf().format(Math.round(diff / 86400), 'day');
  return formatDay(iso);
}

export function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`;
}

export const DOCUMENT_STATUS: Record<DocumentStatus, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'slate' },
  submitted: { label: 'Submitted', tone: 'blue' },
  in_review: { label: 'In review', tone: 'amber' },
  approved: { label: 'Approved', tone: 'green' },
  rejected: { label: 'Rejected', tone: 'red' },
};

export const TEMPLATE_STATUS: Record<TemplateStatus, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'slate' },
  published: { label: 'Published', tone: 'green' },
  archived: { label: 'Archived', tone: 'zinc' },
};

export const FILL_MODE_LABEL: Record<string, string> = {
  strict: 'Fields only',
  free: 'Free editing',
  review: 'Tracked changes',
};

export type Tone = 'slate' | 'blue' | 'amber' | 'green' | 'red' | 'zinc' | 'brand' | 'violet';

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}
